// Personalização do Site — o SERVIDOR (FastAPI + MongoDB) é a única fonte de verdade
// persistente. O site público carrega a configuração na inicialização do app (antes do
// primeiro render), então visitante novo, aba anônima ou outro dispositivo recebe logo,
// banners e Home corretos sem nunca abrir o admin. DEFAULTS é apenas fallback inicial
// quando ainda não existe configuração salva no servidor.
// Imagens da Personalização vão ao armazenamento durável (armazenamento durável do servidor) — a config
// guarda somente URLs estáveis; nada de base64/dataURL no MongoDB nem no localStorage.
import { useEffect, useState } from 'react';
import { getAllProducts, getAllCategories, STORE } from '../data/catalog';
import { uploadImage } from './photoStatus';

const API = process.env.REACT_APP_BACKEND_URL;
const EVENT = 'hud-site-config-changed';

const DEFAULTS = {
  identity: { logoImage: null },
  banners: [],
  store: {
    name: STORE.name,
    phone: STORE.phone,
    whatsapp: STORE.whatsapp,
    whatsappDigits: STORE.whatsappDigits,
    address: 'Praça Osório, 15',
    cityUf: 'Centro · Curitiba/PR',
    instagram: '',
    facebook: '',
    hours: 'Segunda a sexta, 9h às 18h30\nSábado, 9h às 13h30',
    attendanceNote: 'Atendimento especializado para moda, alfaiataria, decoração e artesanato.',
  },
  home: {
    heroTitle: 'Tecidos escolhidos\npara seus projetos',
    heroText:
      'Encontre o tecido que procura, informe a metragem desejada e envie sua solicitação. Confirmamos disponibilidade e valores pelo WhatsApp.',
    heroButtonText: 'EXPLORAR TECIDOS',
    heroDestType: 'todos',
    heroDestValue: '',
    heroImageDesktop: null,
    heroImageMobile: null,
  },
  categories: null,
  featured: null,
  featuredMode: 'auto',
  featuredCount: 12,
};

let cache = {};
let historyCache = [];
let ready = false;
let initPromise = null;

const adminRole = () => {
  try {
    return JSON.parse(localStorage.getItem('hud_admin_user') || 'null')?.role || '';
  } catch {
    return '';
  }
};

// Hidrata o cache a partir do servidor. Chamado uma vez pelo App antes de renderizar.
export function initSiteConfig(force = false) {
  if (initPromise && !force) return initPromise;
  initPromise = (async () => {
    try {
      const res = await fetch(`${API}/api/site-config`);
      if (!res.ok) throw new Error('site-config');
      cache = await res.json();
      ready = true;
      notifySiteConfigChanged();
    } catch (e) {
      console.error('Personalização: API indisponível, usando padrões.', e);
    }
  })();
  return initPromise;
}

export const isSiteConfigReady = () => ready;

// Histórico/auditoria da Personalização (admin) — vem do servidor.
export async function loadSiteHistory() {
  const role = adminRole();
  if (role !== 'admin') return;
  try {
    const res = await fetch(`${API}/api/site-config/history`, { headers: { 'X-Admin-Role': role } });
    if (res.ok) {
      historyCache = await res.json();
      notifySiteConfigChanged();
    }
  } catch {}
}

export function getSiteConfig() {
  const saved = cache || {};
  return {
    identity: { ...DEFAULTS.identity, ...(saved.identity || {}) },
    banners: Array.isArray(saved.banners) ? saved.banners : [],
    store: { ...DEFAULTS.store, ...(saved.store || {}) },
    home: { ...DEFAULTS.home, ...(saved.home || {}) },
    categories: Array.isArray(saved.categories) ? saved.categories : null,
    featured: Array.isArray(saved.featured) ? saved.featured : null,
    // Rotação automática é o padrão; 'manual' volta a usar a lista montada no admin.
    featuredMode: saved.featuredMode === 'manual' ? 'manual' : 'auto',
    featuredCount: Math.max(1, Math.round(Number(saved.featuredCount) || 12)),
  };
}

export function notifySiteConfigChanged() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(EVENT));
}

export function getSiteHistory() {
  return historyCache;
}

// Qualquer imagem ainda em data URL dentro do patch sobe ao armazenamento durável do servidor antes de gravar.
async function uploadDataUrls(value, scope) {
  if (typeof value === 'string' && value.startsWith('data:image')) return uploadImage(value, scope);
  if (Array.isArray(value)) return Promise.all(value.map((v) => uploadDataUrls(v, scope)));
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = await uploadDataUrls(v, scope);
    return out;
  }
  return value;
}

// Salvar no admin: aguarda a confirmação do backend, atualiza o cache compartilhado e
// reflete imediatamente no admin e no site público (evento). Persiste após F5, logout,
// novo navegador ou outro dispositivo.
export async function saveSiteConfig(patch, user, action) {
  const role = adminRole();
  const cleaned = await uploadDataUrls(patch, 'personalizacao');
  const next = { ...getSiteConfig(), ...cleaned };
  const res = await fetch(`${API}/api/site-config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'X-Admin-Role': role },
    body: JSON.stringify({ data: next, action, user: { id: user?.id, name: user?.name } }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || 'Falha ao salvar no servidor.');
  cache = next;
  if (action) {
    historyCache = [
      { at: new Date().toISOString(), userId: user?.id || null, userName: user?.name || null, action, origin: 'PERSONALIZAÇÃO DO SITE' },
      ...historyCache,
    ];
  }
  notifySiteConfigChanged();
}

export function useSiteConfig() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const h = () => setTick((t) => t + 1);
    window.addEventListener(EVENT, h);
    return () => window.removeEventListener(EVENT, h);
  }, []);
  return getSiteConfig();
}

export function resolveDestination(destType, destValue) {
  if (destType === 'categoria' && destValue) return `/categoria/${destValue}`;
  if (destType === 'tecido' && destValue) return `/produto/${destValue}`;
  if (destType === 'loja') return '/nossa-loja';
  return '/categoria/todos';
}

export function getActiveBanners(cfg) {
  return (cfg?.banners || []).filter((b) => b.active && !b.archived).sort((a, b) => (a.order || 0) - (b.order || 0));
}

// Categorias em destaque referenciam o cadastro real da categoria (sem duplicar).
// Todas aparecem na lista de controle; o cadastro define se entra visível por padrão.
// Categorias novas entram como OCULTAS: nunca aparecem na Home sem ação do admin.
export function getHomeCategories(cfg) {
  const conf = cfg?.categories;
  const base = (c, item) => ({
    slug: c.slug,
    name: c.name,
    image: item?.image || c.image || null,
    tagline: item?.tagline ?? c.tagline ?? '',
    show: item ? item.show !== false : c.showOnHome !== false,
    customImage: !!item?.image,
  });
  if (!conf) return getAllCategories().map((c) => base(c, null));
  const items = conf
    .map((item) => {
      const cat = getAllCategories().find((c) => c.slug === item.slug);
      return cat ? base(cat, item) : null;
    })
    .filter(Boolean);
  getAllCategories().forEach((c) => {
    if (!items.some((i) => i.slug === c.slug)) items.push(base(c, null));
  });
  return items;
}

// Tecidos em destaque referenciam o tecido real; sem config salva, usa a flag featured do catálogo.
export function getFeaturedList(cfg) {
  if (cfg?.featured) return cfg.featured;
  return getAllProducts().filter((p) => p.featured).map((p) => ({ slug: p.slug, active: true }));
}

// Sorteio diário determinístico: a semente é a DATA (fuso da loja), então todos os
// visitantes veem os mesmos destaques no dia e a seleção gira o catálogo a cada dia.
const dailySeed = () => {
  const day = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
  let h = 2166136261;
  for (const ch of day) {
    h ^= ch.charCodeAt(0);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const mulberry32 = (a) => () => {
  a |= 0;
  a = (a + 0x6d2b79f5) | 0;
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

export function getFeaturedProducts(cfg) {
  // Modo automático: sorteia N tecidos por dia entre todos os visíveis que têm foto.
  if (cfg?.featuredMode !== 'manual') {
    const count = Math.max(1, Math.round(Number(cfg?.featuredCount) || 12));
    const pool = getAllProducts().filter((p) => p.variants?.[0]?.image || p.image);
    const rand = mulberry32(dailySeed());
    const shuffled = [...pool];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, count);
  }
  return getFeaturedList(cfg)
    .filter((f) => f.active !== false)
    .map((f) => getAllProducts().find((p) => p.slug === f.slug))
    .filter(Boolean);
}

// Redimensiona a imagem no navegador antes do upload ao armazenamento durável
// (fotos grandes de celular chegam otimizadas, sem perda visual relevante do tecido).
export function fileToDataUrl(file, maxWidth = 1600) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Normaliza foto de celular para envio à IA de geração: o navegador aplica a orientação
// EXIF ao desenhar no canvas, converte qualquer formato decodificável para JPEG e reduz
// dimensões/peso (máx. 1400px, alvo ≤ ~1,5 MB) preservando textura/cor/estampa.
// Formatos não decodificáveis pelo navegador (ex.: HEIC fora do Safari) são rejeitados
// com erro específico. A foto original do produto nunca é alterada — isto é só a cópia
// enviada à IA.
export function normalizeImageForAI(file, maxSide = 1400, maxBytes = 1500 * 1024) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        let quality = 0.88;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);
        // base64 infla ~37%: converte o alvo de bytes para tamanho de string
        while (dataUrl.length > maxBytes * 1.37 && quality > 0.6) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL('image/jpeg', quality);
        }
        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error('formato-incompativel'));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error('leitura-falhou'));
    reader.readAsDataURL(file);
  });
}
